// (c) chaehongee. Confidential and proprietary.

import { useCallback, useState, useEffect } from 'react'
import type { Rect, FrameLayer } from '../framing/types'
import type { DragState, FrameIntersection, CompositionAPI } from './types'

interface UseCanvasInternalDragParams {
    frameEditState: Record<string, any>
    worldRectsById: Record<string, Rect>
    initialWorldRectsById: Record<string, Rect>
    imageSizesBySrc: Record<string, { w: number; h: number }>
    addLayer: (frameId: string, layer: FrameLayer) => void
    setWorldRects: (rects: Record<string, Rect>) => void
    removeNode?: (nodeId: string) => void
    sessionId?: string | null
    /** Called after a successful drop with the target frame ID */
    onDropComplete?: (targetFrameId: string) => void
}

function calculateIntersection(rect1: Rect, rect2: Rect): boolean {
    return !(
        rect1.x + rect1.w < rect2.x ||
        rect1.x > rect2.x + rect2.w ||
        rect1.y + rect1.h < rect2.y ||
        rect1.y > rect2.y + rect2.h
    )
}

function calculateOverlapArea(rect1: Rect, rect2: Rect): number {
    const xOverlap = Math.max(0, Math.min(rect1.x + rect1.w, rect2.x + rect2.w) - Math.max(rect1.x, rect2.x))
    const yOverlap = Math.max(0, Math.min(rect1.y + rect1.h, rect2.y + rect2.h) - Math.max(rect1.y, rect2.y))
    return xOverlap * yOverlap
}

export function useCanvasInternalDrag(params: UseCanvasInternalDragParams): CompositionAPI {
    const {
        frameEditState,
        worldRectsById,
        initialWorldRectsById,
        imageSizesBySrc,
        addLayer,
        setWorldRects,
        removeNode,
        sessionId,
        onDropComplete,
    } = params

    const [dragState, setDragStateInternal] = useState<DragState>({
        draggedNodeId: null,
        draggedNodeSrc: null,
        draggedNodeMediaType: null,
        currentPosition: null,
        targetFrameId: null,
        hasCreatedCopy: false,
        originalRect: null,
        ghostRect: null,
        ghostOpacity: 0.7,
        dragStartWorldPos: null,
        returnAnimation: null,
    })

    const setDragState = useCallback((partial: Partial<DragState>) => {
        setDragStateInternal((prev) => ({ ...prev, ...partial }))
    }, [])

    const checkFrameIntersections = useCallback((nodeRect: Rect): FrameIntersection[] => {
        const intersections: FrameIntersection[] = []

        Object.keys(frameEditState).forEach((frameId) => {
            const edit = frameEditState[frameId]
            if (!edit?.currentFrame) return

            const frameRect = edit.currentFrame
            const intersects = calculateIntersection(nodeRect, frameRect)

            intersections.push({
                frameId,
                frameRect,
                intersects,
            })
        })

        return intersections
    }, [frameEditState])

    const handleDrop = useCallback(() => {
        const { draggedNodeId, draggedNodeSrc, targetFrameId, ghostRect, hasCreatedCopy } = dragState

        if (!draggedNodeId || !draggedNodeSrc || !targetFrameId) {
            setDragState({
                draggedNodeId: null,
                draggedNodeSrc: null,
                draggedNodeMediaType: null,
                currentPosition: null,
                targetFrameId: null,
                hasCreatedCopy: false,
                originalRect: null,
                ghostRect: null,
                ghostOpacity: 0.7,
                dragStartWorldPos: null,
                returnAnimation: null,
            })
            return
        }

        const naturalSize = imageSizesBySrc[draggedNodeSrc]

        if (!naturalSize) {
            console.warn('[useCanvasInternalDrag] Cannot drop: missing natural size')
            setDragState({
                draggedNodeId: null,
                draggedNodeSrc: null,
                draggedNodeMediaType: null,
                currentPosition: null,
                targetFrameId: null,
                hasCreatedCopy: false,
                originalRect: null,
                ghostRect: null,
                ghostOpacity: 0.7,
                dragStartWorldPos: null,
                returnAnimation: null,
            })
            return
        }

        const targetFrame = frameEditState[targetFrameId]
        if (!targetFrame) {
            console.warn('[useCanvasInternalDrag] Cannot drop: target frame not found')
            setDragState({
                draggedNodeId: null,
                draggedNodeSrc: null,
                draggedNodeMediaType: null,
                currentPosition: null,
                targetFrameId: null,
                hasCreatedCopy: false,
                originalRect: null,
                ghostRect: null,
                ghostOpacity: 0.7,
                dragStartWorldPos: null,
                returnAnimation: null,
            })
            return
        }

        const maxZIndex = Math.max(0, ...targetFrame.layers.map((l: FrameLayer) => l.zIndex))

        const layerRect = hasCreatedCopy && ghostRect ? ghostRect : worldRectsById[draggedNodeId]
        if (!layerRect) {
            console.warn('[useCanvasInternalDrag] Cannot drop: no rect available')
            setDragState({
                draggedNodeId: null,
                draggedNodeSrc: null,
                draggedNodeMediaType: null,
                currentPosition: null,
                targetFrameId: null,
                hasCreatedCopy: false,
                originalRect: null,
                ghostRect: null,
                ghostOpacity: 0.7,
                dragStartWorldPos: null,
                returnAnimation: null,
            })
            return
        }

        const newLayer: FrameLayer = {
            id: `${draggedNodeId}-layer-${Date.now()}`,
            src: draggedNodeSrc,
            mediaType: dragState.draggedNodeMediaType ?? 'image',
            baseRect: { ...layerRect },
            currentRect: { ...layerRect },
            zIndex: maxZIndex + 1,
        }

        addLayer(targetFrameId, newLayer)
        onDropComplete?.(targetFrameId)

        setDragState({
            draggedNodeId: null,
            draggedNodeSrc: null,
            draggedNodeMediaType: null,
            currentPosition: null,
            targetFrameId: null,
            hasCreatedCopy: false,
            originalRect: null,
            ghostRect: null,
            ghostOpacity: 0.7,
            dragStartWorldPos: null,
            returnAnimation: null,
        })
    }, [dragState, worldRectsById, imageSizesBySrc, initialWorldRectsById, frameEditState, addLayer, removeNode, sessionId, onDropComplete])

    const updateTargetFrame = useCallback(() => {
        if (!dragState.draggedNodeId) return

        const currentNodeRect = dragState.hasCreatedCopy && dragState.ghostRect 
            ? dragState.ghostRect 
            : worldRectsById[dragState.draggedNodeId]
        
        if (!currentNodeRect) return

        const intersections = checkFrameIntersections(currentNodeRect)
        
        const intersectingFrames = intersections.filter((f) => {
            return f.intersects && f.frameId !== dragState.draggedNodeId
        })

        if (intersectingFrames.length === 0) {
            if (dragState.targetFrameId !== null) {
                setDragState({ targetFrameId: null })
            }
            return
        }

        let bestFrame = intersectingFrames[0]
        let maxOverlap = calculateOverlapArea(currentNodeRect, bestFrame.frameRect)

        for (const frame of intersectingFrames.slice(1)) {
            const overlap = calculateOverlapArea(currentNodeRect, frame.frameRect)
            if (overlap > maxOverlap) {
                maxOverlap = overlap
                bestFrame = frame
            }
        }

        const wasNotTargeting = dragState.targetFrameId === null
        const isNowTargeting = bestFrame.frameId !== null

        if (dragState.targetFrameId !== bestFrame.frameId) {
            const updates: Partial<DragState> = { targetFrameId: bestFrame.frameId }
            
            if (wasNotTargeting && isNowTargeting && !dragState.hasCreatedCopy) {
                // "Original position" for snap-back must be the pre-drag rect,
                // not the initial baseline placement.
                const originalRect =
                    dragState.originalRect ||
                    worldRectsById[dragState.draggedNodeId] ||
                    initialWorldRectsById[dragState.draggedNodeId]
                if (originalRect) {
                    updates.hasCreatedCopy = true
                    updates.originalRect = { ...originalRect }
                    updates.ghostRect = { ...currentNodeRect }
                    updates.ghostOpacity = 0.7
                    updates.returnAnimation = {
                        isAnimating: true,
                        fromRect: { ...currentNodeRect },
                        toRect: { ...originalRect },
                        startTime: Date.now(),
                    }
                    
                    setWorldRects({
                        ...worldRectsById,
                        [dragState.draggedNodeId]: { ...originalRect }
                    })
                }
            }
            
            setDragState(updates)
        }
    }, [dragState.draggedNodeId, dragState.targetFrameId, dragState.hasCreatedCopy, dragState.ghostRect, dragState.originalRect, worldRectsById, initialWorldRectsById, checkFrameIntersections, setDragState, setWorldRects])

    const updateGhostPosition = useCallback((delta: { x: number; y: number }) => {
        if (!dragState.hasCreatedCopy || !dragState.ghostRect) return
        
        setDragState({
            ghostRect: {
                ...dragState.ghostRect,
                x: dragState.ghostRect.x + delta.x,
                y: dragState.ghostRect.y + delta.y,
            }
        })
    }, [dragState.hasCreatedCopy, dragState.ghostRect, setDragState])

    const clearReturnAnimation = useCallback(() => {
        setDragState({ returnAnimation: null })
    }, [setDragState])

    useEffect(() => {
        if (dragState.draggedNodeId) {
            updateTargetFrame()
        }
    }, [dragState.draggedNodeId, worldRectsById, updateTargetFrame])

    return {
        dragState,
        setDragState,
        checkFrameIntersections,
        handleDrop,
        updateGhostPosition,
        clearReturnAnimation,
    }
}

export default useCanvasInternalDrag

